import { useState, useCallback } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import type { MapData } from '../../types';
import StateTooltip from './StateTooltip';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const FIPS_TO_STATE: Record<string, string> = {
  '01': 'AL', '02': 'AK', '04': 'AZ', '05': 'AR', '06': 'CA',
  '08': 'CO', '09': 'CT', '10': 'DE', '11': 'DC', '12': 'FL',
  '13': 'GA', '15': 'HI', '16': 'ID', '17': 'IL', '18': 'IN',
  '19': 'IA', '20': 'KS', '21': 'KY', '22': 'LA', '23': 'ME',
  '24': 'MD', '25': 'MA', '26': 'MI', '27': 'MN', '28': 'MS',
  '29': 'MO', '30': 'MT', '31': 'NE', '32': 'NV', '33': 'NH',
  '34': 'NJ', '35': 'NM', '36': 'NY', '37': 'NC', '38': 'ND',
  '39': 'OH', '40': 'OK', '41': 'OR', '42': 'PA', '44': 'RI',
  '45': 'SC', '46': 'SD', '47': 'TN', '48': 'TX', '49': 'UT',
  '50': 'VT', '51': 'VA', '53': 'WA', '54': 'WV', '55': 'WI',
  '56': 'WY',
};

interface TooltipState {
  x: number;
  y: number;
  stateCode: string;
}

interface CoverageMapProps {
  mapData: MapData | null;
  selectedRegionId: string | null;
  onRegionSelect: (regionId: string | null) => void;
  onStateClick?: (stateCode: string) => void;
}

export default function CoverageMap({ mapData, selectedRegionId, onRegionSelect, onStateClick }: CoverageMapProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent, stateCode: string) => {
      if (!mapData || !stateCode) return;

      const containerRect = (event.currentTarget as SVGElement)
        .closest('.coverage-map-container')
        ?.getBoundingClientRect();

      const x = event.clientX - (containerRect?.left ?? 0);
      const y = event.clientY - (containerRect?.top ?? 0);

      setTooltip({ x, y, stateCode });
    },
    [mapData]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent, stateCode: string) => {
      if (!tooltip || tooltip.stateCode !== stateCode) return;
      const containerRect = (event.currentTarget as SVGElement)
        .closest('.coverage-map-container')
        ?.getBoundingClientRect();
      const x = event.clientX - (containerRect?.left ?? 0);
      const y = event.clientY - (containerRect?.top ?? 0);
      setTooltip((prev) => (prev ? { ...prev, x, y } : null));
    },
    [tooltip]
  );

  const handleMouseLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  const handleStateClick = useCallback(
    (stateCode: string) => {
      if (!stateCode) return;
      if (onStateClick) {
        onStateClick(stateCode);
      } else {
        // Fallback: toggle region selection
        if (!mapData) return;
        const coverage = mapData.stateCoverage[stateCode];
        if (!coverage) return;
        const newRegionId = coverage.regionId;
        onRegionSelect(selectedRegionId === newRegionId ? null : newRegionId);
      }
    },
    [mapData, selectedRegionId, onRegionSelect, onStateClick]
  );

  const getStateColor = useCallback(
    (stateCode: string): string => {
      if (!mapData) return '#E5E7EB';
      const coverage = mapData.stateCoverage[stateCode];
      if (!coverage) return '#E5E7EB';

      if (selectedRegionId !== null && coverage.regionId !== selectedRegionId) {
        return '#D1D5DB'; // dimmed
      }

      return coverage.regionColor;
    },
    [mapData, selectedRegionId]
  );

  const getStateOpacity = useCallback(
    (stateCode: string): number => {
      if (!mapData) return 1;
      const coverage = mapData.stateCoverage[stateCode];
      if (!coverage) return 1;
      if (selectedRegionId !== null && coverage.regionId !== selectedRegionId) {
        return 0.4;
      }
      return 1;
    },
    [mapData, selectedRegionId]
  );

  return (
    <div className="coverage-map-container relative bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
      <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 800 }}
        style={{ width: '100%', height: '100%' }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const fips = geo.id?.toString().padStart(2, '0') ?? '';
              const stateCode = FIPS_TO_STATE[fips] ?? '';
              const fill = getStateColor(stateCode);
              const opacity = getStateOpacity(stateCode);
              const hasCoverage = !!(mapData?.stateCoverage[stateCode]);

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={fill}
                  stroke="#FFFFFF"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none', opacity },
                    hover: {
                      outline: 'none',
                      opacity,
                      fill: hasCoverage ? fill : '#D1D5DB',
                      filter: hasCoverage ? 'brightness(0.9)' : 'none',
                      cursor: 'pointer',
                    },
                    pressed: { outline: 'none', opacity },
                  }}
                  onMouseEnter={(e) => handleMouseEnter(e, stateCode)}
                  onMouseMove={(e) => handleMouseMove(e, stateCode)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => handleStateClick(stateCode)}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {tooltip && (
        <StateTooltip
          position={{ x: tooltip.x, y: tooltip.y }}
          stateCode={tooltip.stateCode}
          coverage={mapData?.stateCoverage[tooltip.stateCode] ?? null}
        />
      )}
    </div>
  );
}
